import React, { useEffect, useState } from 'react';
import { Edit2, Save, X, Plus, Minus, RefreshCw, Search, Filter, User } from 'lucide-react';
import localRequest from '../../services/LocalServer/localRequest';
import noimg from '../../Icons/checked.svg';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const ProductInventory = () => {
  const history = useHistory();
  const machineID = localStorage.getItem('machineID');

  const testMode = localStorage.getItem('testMode') === 'true' ? 'true' : 'false';
  const linkToURL = `/machineUI/home/${machineID}/${testMode}`;

  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'fridge1', 'fridge2'
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = () => {
    setIsLoading(true);
    localRequest({
      method: 'get',
      url: `getProduct`,
    })
      .then((res) => {
        const result = res.data.filter(
          (item) => item.prodType === 'fridge1' || item.prodType === 'fridge2'
        );
        console.log('result', result);
        // Transform the API data to match the component's expected format
        const transformedProducts = result.map((item) => ({
          uid: item.uid,
          id: item._id, // Keep id for any legacy references, but uid is primary
          name: item.name.en || 'Unnamed Product',
          upc: item.upc || '',
          price: item.price || 0,
          current_stock: item.current_stock || 0,
          max_stock: item.max_stock || 100, // Default max stock if not provided
          prodType: item.prodType,
          brand: item.brand,
          description: item.description?.en || '',
          productPictures: item.productPictures || [],
        }));

        setProducts(transformedProducts);
        setOriginalProducts([...transformedProducts]);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
        setIsLoading(false);
        // You might want to show an error message to the user
      });
  };

  const updateStock = (uid, newStock) => {
    if (newStock < 0) return;

    setProducts((prevProducts) =>
      prevProducts.map((product) => {
        if (product.uid === uid) {
          const updatedProduct = { ...product, current_stock: newStock };
          return updatedProduct;
        }
        return product;
      })
    );
    setHasChanges(true);
  };

  const incrementStock = (uid) => {
    const product = products.find((p) => p.uid === uid);
    updateStock(uid, product.current_stock + 1);
  };

  const decrementStock = (uid) => {
    const product = products.find((p) => p.uid === uid);
    updateStock(uid, Math.max(0, product.current_stock - 1));
  };

  const startEdit = (uid, currentStock) => {
    setEditingId(uid);
    setEditValue(currentStock.toString());
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = (uid) => {
    const newStock = parseInt(editValue);
    if (isNaN(newStock) || newStock < 0) {
      alert('Please enter a valid stock number (0 or greater)');
      return;
    }

    updateStock(uid, newStock);
    setEditingId(null);
    setEditValue('');
  };

  const saveAllChanges = () => {
    setOriginalProducts([...products]);
    setHasChanges(false);

    const result = products.map((product) => {
      return {
        current_stock: product.current_stock,
        uid: product.uid,
        upc: product.upc,
      };
    });

    localRequest({
      method: 'patch',
      url: 'updateFridgeProduct',
      data: result,
    })
      .then((res) => {
        console.log('res', res);
        Swal.fire({
          title: 'Changes saved successfully',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
        });
      })
      .catch((error) => {
        console.error('Error updating products:', error);
        Swal.fire({
          title: 'Failed to save changes',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500,
        });
        // You might want to show an error message to the user
      });

    // Here you might want to send the updated data back to the server
    // You could iterate through products and send update requests
  };

  const discardChanges = () => {
    setProducts([...originalProducts]);
    setHasChanges(false);
    setEditingId(null);
    setEditValue('');
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.upc.includes(searchTerm);

    if (filterType === 'all') return matchesSearch;

    return matchesSearch && product.prodType === filterType;
  });

  const backToUserMode = () => {
    history.push(linkToURL);
    // This would typically navigate to a different component/page
  };

  if (isLoading) {
    return (
      <div
        className="h-screen w-full bg-gray-100 flex items-center justify-center"
        style={{ width: '1080px', height: '1920px' }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-full bg-gray-100 overflow-hidden flex flex-col"
      style={{ width: '1080px', height: '1920px' }}
    >
      {/* Header */}
      <div className="bg-white shadow-sm border-b p-4 flex-shrink-0">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Product Inventory</h1>
            <p className="text-gray-600">
              {filteredProducts.length} of {products.length} Products
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={backToUserMode}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium"
            >
              <User size={16} className="inline mr-1" />
              User Mode
            </button>
            {hasChanges && (
              <button
                onClick={discardChanges}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded font-medium"
              >
                <RefreshCw size={16} className="inline mr-1" />
                Discard
              </button>
            )}
            <button
              onClick={saveAllChanges}
              disabled={!hasChanges}
              className={`px-4 py-2 rounded font-medium ${
                hasChanges
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Save size={16} className="inline mr-1" />
              Save All
            </button>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex gap-4 items-center">
          {/* Search is commented out in your original code, keeping it that way */}
          {/* <div className="relative flex-1 max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search by product name or UPC..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div> */}

          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-2 rounded font-medium text-sm ${
                filterType === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              All Products
            </button>
            <button
              onClick={() => setFilterType('fridge1')}
              className={`px-3 py-2 rounded font-medium text-sm ${
                filterType === 'fridge1'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Filter size={14} className="inline mr-1" />
              Fridge 1
            </button>
            <button
              onClick={() => setFilterType('fridge2')}
              className={`px-3 py-2 rounded font-medium text-sm ${
                filterType === 'fridge2'
                  ? 'bg-teal-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <Filter size={14} className="inline mr-1" />
              Fridge2/Shelf
            </button>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <div key={product.uid} className="bg-white rounded-lg shadow-sm border p-4">
                <div className="flex gap-3 mb-3">
                  <div className="flex-shrink-0">
                    <img
                      src={
                        product.productPictures && product.productPictures.length > 0
                          ? `${process.env.REACT_APP_LOCAL_API}/pictures/fridge/${product.productPictures[0]}`
                          : noimg
                      }
                      alt={product.name}
                      className="w-14 h-18 object-cover rounded-md bg-gray-100"
                      onError={(e) => {
                        e.target.src = noimg;
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800 text-sm mb-1 truncate">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 font-mono mb-1">{product.upc}</p>
                    <p className="text-lg font-bold text-gray-800">${product.price.toFixed(2)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Current Stock:</span>
                      <div className="flex items-center gap-2">
                        {editingId === product.uid ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-16 px-2 py-1 border rounded text-sm text-center"
                              min="0"
                              autoFocus
                            />
                            <button
                              onClick={() => saveEdit(product.uid)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                            >
                              <Save size={12} />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-1 text-gray-400 hover:bg-gray-50 rounded"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span className="font-bold text-gray-800">{product.current_stock}</span>
                            <button
                              onClick={() => startEdit(product.uid, product.current_stock)}
                              className="p-1 text-gray-400 hover:bg-gray-50 rounded"
                            >
                              <Edit2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-center gap-1">
                      <button
                        onClick={() => decrementStock(product.uid)}
                        className="p-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
                        disabled={product.current_stock === 0}
                      >
                        <Minus size={12} />
                      </button>
                      <button
                        onClick={() => incrementStock(product.uid)}
                        className="p-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs"
                      >
                        <Plus size={12} />
                      </button>
                    </div>

                    <div className="text-xs text-center">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          product.prodType === 'fridge1'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-teal-100 text-teal-700'
                        }`}
                      >
                        {product.prodType === 'fridge1' ? 'Fridge 1' : 'Fridge2/Shelf'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductInventory;
